import React, { useEffect, useCallback, useReducer, useContext, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { CircularProgress, Card } from "@material-ui/core";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { dataFetchReducer } from "../Utils/requestUtils";
import AuthContext from "../../context/auth-context";

const REFRESH_INTERVAL_SECONDS = 60;
const TICK_MILLISECONDS = 1000;
const PERIOD_OPTIONS = [24, 168];
const MAIN_COLOR = "#0b1f82";
const STATUS_COLORS = {
    available: "#2e7d32",
    unavailable: "#c62828",
    unknown: "#757575"
};
const CATEGORY_COLORS = {
    provider_unavailable: "#c62828",
    provider_degraded: "#ef6c00"
};

const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    return moment(isoDate).format("DD/MM/YYYY HH:mm:ss");
};

const formatDuration = (durationSeconds) => {
    const totalMinutes = Math.floor(durationSeconds / 60);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    if (minutes > 0) return `${minutes}min`;
    return "< 1min";
};

function StatusPage() {
    const { t } = useTranslation();
    const history = useHistory();
    const user_data = useContext(AuthContext).user_data;

    if (!user_data.roles.includes("read_credit_analysis_data_source_status")) {
        history.push("");
    }

    const [hours, setHours] = useState(PERIOD_OPTIONS[0]);
    const [pageState, dispatchPageState] = useReducer(
        dataFetchReducer,
        { fetchedData: null, isLoading: true, isError: false }
    );
    const [secondsToRefresh, setSecondsToRefresh] = useState(REFRESH_INTERVAL_SECONDS);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const secondsToRefreshRef = useRef(REFRESH_INTERVAL_SECONDS);

    const doRequest = useCallback((selectedHours) => {
        axios.get("/dash/credit_analysis/data_source/indicators", {
            params: { hours: selectedHours }
        }).then(response => {
            dispatchPageState({ type: "data_fetch_success", payload: response.data });
            setLastUpdatedAt(new Date());
        }).catch(error => {
            if ((error.response || {}).status === 403) dispatchPageState({ type: "data_fetch_failure_403" });
            else if ((error.response || {}).status === 404) dispatchPageState({ type: "data_fetch_failure_404" });
            else dispatchPageState({ type: "data_fetch_failure" });
        });
    }, []);

    useEffect(() => {
        dispatchPageState({ type: "data_fetch_init" });
        doRequest(hours);
        secondsToRefreshRef.current = REFRESH_INTERVAL_SECONDS;
        setSecondsToRefresh(REFRESH_INTERVAL_SECONDS);
        const timer = setInterval(() => {
            secondsToRefreshRef.current -= 1;
            if (secondsToRefreshRef.current <= 0) {
                doRequest(hours);
                secondsToRefreshRef.current = REFRESH_INTERVAL_SECONDS;
            }
            setSecondsToRefresh(secondsToRefreshRef.current);
        }, TICK_MILLISECONDS);
        return () => {
            clearInterval(timer);
        };
    }, [doRequest, hours]);

    const liveIndicator = (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "20px" }}>
            <div style={{ position: "relative", width: "22px", height: "22px" }}>
                <CircularProgress
                    variant="determinate"
                    value={((REFRESH_INTERVAL_SECONDS - secondsToRefresh) / REFRESH_INTERVAL_SECONDS) * 100}
                    size={22}
                    thickness={5}
                    style={{ color: STATUS_COLORS.available, position: "absolute", top: 0, left: 0 }}
                />
                <span style={{
                    position: "absolute",
                    top: "7px",
                    left: "7px",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: STATUS_COLORS.available
                }} />
            </div>
            <div className={["normalText", "normalMediumSize"].join(" ")}>
                <div className="bold" style={{ color: STATUS_COLORS.available }}>
                    {t("status_page_live_refresh", { seconds: secondsToRefresh })}
                </div>
                <div>
                    {lastUpdatedAt ? t("status_page_last_update", { time: moment(lastUpdatedAt).format("HH:mm:ss") }) : "-"}
                </div>
            </div>
        </div>
    );

    const pageTitle = (
        <div style={{ display: "flex", alignItems: "center" }}>
            <p className="blueText titleSize">{t("status_page_title")}</p>
            {liveIndicator}
            <div style={{ margin: "auto 20px auto auto", display: "flex" }}>
                {PERIOD_OPTIONS.map(option => (
                    <div
                        key={option}
                        style={{ padding: "5px 20px", margin: "5px", opacity: option === hours ? 1 : 0.5 }}
                        className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                        onClick={() => setHours(option)}
                    >
                        {t(`data_source_status_period_${option}`)}
                    </div>
                ))}
            </div>
        </div>
    );

    const errorCodeLabel = (code) => t("data_source_status_code_label", {
        name: t(`data_source_status_code_name_${code}`, { defaultValue: t("data_source_status_code_name_default") }),
        code: code
    });

    const errorCodeExplanation = (code) => t(`data_source_status_code_explanation_${code}`, {
        defaultValue: t("data_source_status_code_explanation_default")
    });

    const timelineEvent = (label, isoDate, color) => (
        <div style={{ textAlign: "center", flex: "0 1 220px" }}>
            <div style={{ width: "14px", height: "14px", borderRadius: "50%", backgroundColor: color, margin: "0 auto 8px auto" }} />
            <div className={["normalText", "normalMediumSize"].join(" ")}>{t(label)}</div>
            <div className={["bold", "subtitleSize"].join(" ")}>{formatDate(isoDate)}</div>
        </div>
    );

    const instabilitySummary = (instability) => {
        if (!instability) {
            return (
                <div className={["normalText", "normalMediumSize"].join(" ")} style={{ marginTop: "6px" }}>
                    {t("data_source_status_no_instability")}
                </div>
            );
        }
        const color = instability.ongoing ? STATUS_COLORS.unavailable : STATUS_COLORS.available;
        const label = instability.ongoing ? "data_source_status_instability_ongoing" : "data_source_status_instability_ended";
        return (
            <div style={{ marginTop: "6px" }}>
                <div className={["bold", "subtitleSize"].join(" ")} style={{ color: color }}>
                    {t(label, { duration: formatDuration(instability.duration_seconds) })}
                </div>
                <div className={["normalText", "normalMediumSize"].join(" ")}>
                    {t("data_source_status_instability_started_at", { date: formatDate(instability.started_at) })}
                </div>
            </div>
        );
    };

    const timeline = (indicators) => {
        const successEvent = { label: "data_source_status_last_success", date: indicators.last_success_at, color: STATUS_COLORS.available };
        const errorEvent = { label: "data_source_status_last_error", date: (indicators.last_error || {}).occurred_at, color: STATUS_COLORS.unavailable };
        let events = [successEvent, errorEvent];
        if (successEvent.date && errorEvent.date && moment(errorEvent.date).isBefore(moment(successEvent.date))) {
            events = [errorEvent, successEvent];
        }
        return (
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", marginTop: "20px" }}>
                {timelineEvent(events[0].label, events[0].date, events[0].color)}
                <div style={{ flex: "1 1 240px", maxWidth: "360px", margin: "0 12px", paddingTop: "2px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <div style={{ flex: "1", borderTop: `2px dashed ${MAIN_COLOR}`, opacity: 0.4 }} />
                        <span style={{ margin: "0 6px", color: MAIN_COLOR }}>&#9658;</span>
                    </div>
                    {instabilitySummary(indicators.instability)}
                </div>
                {timelineEvent(events[1].label, events[1].date, events[1].color)}
            </div>
        );
    };

    const statusCard = (indicators) => {
        const statusColor = STATUS_COLORS[indicators.status] || STATUS_COLORS.unknown;
        return (
            <Card style={{ padding: "24px", margin: "10px 0", borderTop: `4px solid ${statusColor}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ width: "16px", height: "16px", borderRadius: "50%", marginRight: "12px", backgroundColor: statusColor }} />
                    <div style={{ textAlign: "center" }}>
                        <div className={["normalText", "subtitleSize"].join(" ")}>
                            {`${indicators.data_source.name.toUpperCase()} · ${indicators.data_source.provider}`}
                        </div>
                        <div className={["bold", "titleSize"].join(" ")} style={{ color: statusColor }}>
                            {t(`data_source_status_${indicators.status}`)}
                        </div>
                    </div>
                </div>
                {timeline(indicators)}
                {indicators.last_error ?
                    <div className={["normalText", "normalMediumSize"].join(" ")} style={{ textAlign: "center", marginTop: "16px" }}>
                        <span className="bold">{errorCodeLabel(indicators.last_error.code)}</span>
                        {` · ${errorCodeExplanation(indicators.last_error.code)}`}
                    </div>
                    : null}
            </Card>
        );
    };

    const instabilityRateCard = (instabilityRate) => (
        <div style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
            <Card
                style={{
                    minWidth: "240px",
                    padding: "20px 16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderTop: `4px solid ${STATUS_COLORS.unavailable}`
                }}
            >
                <div style={{ fontSize: "32px", fontWeight: 700, color: STATUS_COLORS.unavailable, lineHeight: 1.2 }}>
                    {`${(instabilityRate * 100).toFixed(1)}%`}
                </div>
                <div className={["normalText", "normalMediumSize"].join(" ")} style={{ marginTop: "6px", textAlign: "center" }}>
                    {t("data_source_status_instability_rate")}
                </div>
            </Card>
        </div>
    );

    const errorCard = (error) => {
        const categoryColor = CATEGORY_COLORS[error.category] || STATUS_COLORS.unknown;
        return (
            <Card key={error.code} style={{ padding: "16px 20px", margin: "10px 0", borderLeft: `4px solid ${categoryColor}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <div style={{ flex: "1 1 auto" }}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                            <span className={["bold", "subtitleSize"].join(" ")}>{errorCodeLabel(error.code)}</span>
                            <span
                                className="normalMediumSize"
                                style={{ padding: "2px 10px", borderRadius: "12px", color: "#FFF", backgroundColor: categoryColor }}
                            >
                                {t(`data_source_status_category_${error.category}`)}
                            </span>
                        </div>
                        <div className={["normalText", "normalMediumSize"].join(" ")} style={{ marginTop: "6px" }}>
                            {errorCodeExplanation(error.code)}
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    const errorsByCodeList = (errorsByCode) => (
        <div style={{ margin: "24px 0" }}>
            <div className={["blueText", "subtitleSize"].join(" ")}>
                {t("data_source_status_errors_by_code_title")}
            </div>
            <div className={["normalText", "normalMediumSize"].join(" ")} style={{ margin: "6px 0 12px 0" }}>
                {t("data_source_status_errors_by_code_intro")}
            </div>
            {errorsByCode.length === 0 ?
                <Card style={{ padding: "20px", textAlign: "center" }}>
                    <div className={["normalText", "normalMediumSize"].join(" ")}>{t("data_source_status_no_errors")}</div>
                </Card>
                : errorsByCode.map(error => errorCard(error))}
        </div>
    );

    if (pageState.fetchedData) {
        const indicators = pageState.fetchedData;
        return (
            <div style={{ width: "auto", paddingBottom: "30px" }}>
                {pageTitle}
                {statusCard(indicators)}
                {instabilityRateCard(indicators.instability_rate)}
                {errorsByCodeList(indicators.errors_by_code)}
            </div>
        );
    }
    else if (pageState.isError) {
        return (
            <div style={{ width: "auto" }}>
                {pageTitle}
                <div style={{ height: "55vh", display: "flex" }}>
                    <p className={["blueText", "titleSize"].join(" ")} style={{ margin: "auto", textAlign: "center" }}>
                        {t(pageState.errorMessage)}
                    </p>
                </div>
            </div>
        );
    }
    else if (pageState.isLoading) {
        return (
            <div style={{ width: "auto" }}>
                {pageTitle}
                <div className="circularProgressCenter">
                    <CircularProgress />
                </div>
            </div>
        );
    }
    else return null;
}

export default StatusPage;
